import { useCallback, useEffect, useRef } from "react";
import "../css/FoodList.css";
import { ArrowBigLeft, ArrowBigRight } from "lucide-react";

export default function FoodList({ data, getColor, onSelectItem, onOpenDetail, loading, selectedItem }) {
    const containerRef = useRef(null);
    const autoScrollRef = useRef(null);
    const pauseTimeoutRef = useRef(null);

    const scrollContainer = (direction) => {
        if (!containerRef.current) {
            return;
        }

        const scrollAmount = containerRef.current.clientWidth * 0.8;
        containerRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };

    const clearAutoScroll = useCallback(() => {
        if (autoScrollRef.current) {
            window.clearInterval(autoScrollRef.current);
            autoScrollRef.current = null;
        }
    }, []);

    const startAutoScroll = useCallback(() => {
        clearAutoScroll();

        const container = containerRef.current;
        if (!container) {
            return;
        }

        autoScrollRef.current = window.setInterval(() => {
            const maxScrollLeft = container.scrollWidth - container.clientWidth;
            if (maxScrollLeft <= 0) {
                return;
            }

            const nextLeft = container.scrollLeft + container.clientWidth * 0.7;
            if (nextLeft >= maxScrollLeft - 4) {
                container.scrollTo({ left: 0, behavior: 'smooth' });
                return;
            }

            container.scrollTo({ left: nextLeft, behavior: 'smooth' });
        }, 3500);
    }, [clearAutoScroll]);

    const pauseAutoScroll = () => {
        clearAutoScroll();

        if (pauseTimeoutRef.current) {
            window.clearTimeout(pauseTimeoutRef.current);
        }

        pauseTimeoutRef.current = window.setTimeout(() => {
            startAutoScroll();
        }, 5000);
    };

    useEffect(() => {
        if (loading || data.length <= 1) {
            return undefined;
        }

        startAutoScroll();

        return () => {
            clearAutoScroll();
            if (pauseTimeoutRef.current) {
                window.clearTimeout(pauseTimeoutRef.current);
            }
        };
    }, [data, loading, startAutoScroll, clearAutoScroll]);

    if (loading) {
        return <p className="food-loading">卡池装填中...</p>;
    }

    return (
        <div className="food-list">
            {data.length > 0 ? (
                <div className="food-list-wrapper">
                    <button
                        className="scroll-button scroll-left"
                        onClick={() => {
                            pauseAutoScroll();
                            scrollContainer('left');
                        }}
                        aria-label="向左滚动"
                    >
                        <ArrowBigLeft style={{ color: "red" }} />
                    </button>
                    <div
                        className="food-list-container"
                        ref={containerRef}
                        onPointerDown={pauseAutoScroll}
                        onTouchStart={pauseAutoScroll}
                        onWheel={pauseAutoScroll}
                    >
                        {data.map((item) => {
                            const isSelected = selectedItem?.id === item.id;

                            return (
                                <div
                                    key={item.id}
                                    className={`food-item ${isSelected ? 'food-item-selected' : ''}`}
                                    onClick={() => onSelectItem(item)}
                                >
                                    <div className="food-image-box">
                                        {item.cuisineBadge ? <span className="food-cuisine-badge">{item.cuisineBadge}</span> : null}
                                        <img className="food-image" src={item.image} alt={item.name} />
                                    </div>
                                    <div className="food-item-meta">
                                        <div className="food-name" style={{ color: getColor(item.star) }}>
                                            {item.name}
                                        </div>
                                        {item.subtitle && <div className="food-subtitle">{item.subtitle}</div>}
                                        <div className="food-stars" style={{ color: getColor(item.star) }}>
                                            {Array.from({ length: item.star }).map((_, index) => (
                                                <span key={index}>★</span>
                                            ))}
                                        </div>
                                        {item.tags?.length ? (
                                            <div className="food-tags">
                                                {item.tags.slice(0, 2).map((tag) => (
                                                    <span key={tag} className="food-tag">{tag}</span>
                                                ))}
                                            </div>
                                        ) : null}
                                        {item.detailUrl ? (
                                            <button
                                                className="food-detail-button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onOpenDetail(item);
                                                }}
                                            >
                                                查看做法
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <button
                        className="scroll-button scroll-right"
                        onClick={() => {
                            pauseAutoScroll();
                            scrollContainer('right');
                        }}
                        aria-label="向右滚动"
                    >
                        <ArrowBigRight style={{ color: "red" }} />
                    </button>
                </div>
            ) : (
                <p className="food-loading">这个卡池还没有内容。</p>
            )}
        </div>
    );
}
